/**
 * Boarding identity + view-model types.
 *
 * Re-exported from `@/type/healthcare` (see `index.ts`), so every existing
 * `import type { … } from "@/type/healthcare"` keeps working unchanged.
 *
 * ─── Why branded ids ─────────────────────────────────────────────────────────
 * A boarding "unit" carries TWO different Frappe doc names:
 *   • a `Service Room` name  — the physical room
 *   • a `Pet Boarding` name  — the stay/record currently in that room (may be absent)
 *
 * Record endpoints (`check_in`, `check_out`, `cancel_boarding`, `reserve_room` assign,
 * `create_order`, `report_pet_death`) accept ONLY the latter. Historically the call
 * sites resolved `boardingRecordId || unit.id`, and `unit.id` is room-first — so an
 * empty room silently sent a ROOM name where a BOARDING name was required.
 * That was Tier-1 #1 in docs/BOARDING_GAPS.md.
 *
 * Plain `string` (or even a required-but-unbranded field) cannot prevent that: the two
 * are structurally identical, so the compiler happily accepts either. Branding makes
 * them mutually unassignable at zero runtime cost — the brand exists only in the type
 * system, and `RoomId`/`BoardingRecordId` remain ordinary strings at runtime, so they
 * serialize into request payloads exactly as before.
 */

import type { BoardingOccupancyStatus, BoardingRecordStatus } from "./index";

declare const RoomIdBrand: unique symbol;
declare const BoardingRecordIdBrand: unique symbol;

/**
 * A `Service Room` doc name — the physical room.
 * NEVER accepted by a boarding-record endpoint. Use for room lookups and navigation only.
 */
export type RoomId = string & { readonly [RoomIdBrand]: true };

/**
 * A `Pet Boarding` doc name — the stay record.
 * The ONLY id `check_in` / `check_out` / `cancel_boarding` / `reserve_room` (assign) /
 * `create_order` / `report_pet_death` / `record_medication_given` / `dispense_medication` accept.
 */
export type BoardingRecordId = string & { readonly [BoardingRecordIdBrand]: true };

/** Narrow an unknown value to a `RoomId`. Blank / non-string → `null` (never `""`). */
export function asRoomId(value: unknown): RoomId | null {
  const trimmed = typeof value === "string" ? value.trim() : "";

  return trimmed ? (trimmed as RoomId) : null;
}

/** Narrow an unknown value to a `BoardingRecordId`. Blank / non-string → `null` (never `""`). */
export function asBoardingRecordId(value: unknown): BoardingRecordId | null {
  const trimmed = typeof value === "string" ? value.trim() : "";

  return trimmed ? (trimmed as BoardingRecordId) : null;
}

// ─── Room grouping / hub view models ─────────────────────────────────────────

/**
 * The two groups the boarding desk thinks in. Derived CLIENT-SIDE from the `boarding_type` of
 * the stay(s) in a room — NOT from any property of the room itself. See `roomStayTypeGroups()`
 * in `@/constants/boarding`.
 *
 * ⚠️ `"boarding"` is the key for **Travel** stays; the union member was left unrenamed so the
 * filter state and the locale keys did not have to move.
 */
export type BoardingRoomGroup = "boarding" | "treatment";

/**
 * Summary figures for ONE stay-type group (Travel / Treatment).
 *
 * ⚠️ NO LONGER MIRRORS `BoardingKpiSet`, and the two must not be re-merged. A group counts only
 * the rooms HOSTING a stay of its type, so the whole-board buckets are meaningless here: the old
 * `available` was structurally 0 (a room hosting a stay is not available) and `occupied` merely
 * restated `total`. The three figures below all differ from one another on real data.
 */
export interface BoardingGroupKpis {
  /**
   * Rooms currently HOSTING a stay of this type — **not** "rooms that belong to this group".
   *
   * ⚠️ A ROOM HAS NO GROUP. The group is a property of the STAY in it, so an empty room is on
   * neither card and the two card figures sum to the rooms IN USE, not to the board. Measured
   * live 2026-09-08: 14 + 14 = 28 against a header total of 35, the 7-room gap being exactly the
   * free rooms. The tile is therefore labelled "Rooms in Use", not "Rooms" — the old label read as
   * a share of the whole board and made the two screens look like they contradicted each other.
   *
   * A room holding stays of BOTH types is counted on both cards, so the sum can also exceed the
   * rooms in use. (It did not on the measured board — 14 + 14 landing exactly on 28 means no room
   * was mixed that day.)
   */
  roomsInUse: number;
  /** Animals in those rooms. Diverges from `roomsInUse` the moment a room holds more than one pet. */
  pets: number;
}

/**
 * The hub's KPI strip.
 *
 * Every figure is counted client-side from the already-loaded room list; the backend sends no
 * statistics of its own. Three former tiles were removed as unmeasurable rather than restyled —
 * see the header note in `useBoardingKpis`.
 */
export interface BoardingKpiSet {
  /** Every active room on the board. */
  total: number;
  occupied: number;
  available: number;
  /** `occupied / total` as a whole number 0–100; `total === 0` → 0. */
  occupiedPct: number;
  /** Animals physically in the building — the sum of every room's headcount, not a room count. */
  petsInHouse: number;
  /** Stays whose `check_in` falls on today, local time. */
  arrivalsToday: number;
  /** Rooms whose headcount exceeds the capacity the room itself reported. */
  overCapacity: number;
  /**
   * How many rooms reported a usable capacity at all.
   *
   * ⚠️ THIS IS THE OVER-CAPACITY TILE'S OWN KILL SWITCH, evaluated against live data on every
   * load. `Service Room.capacity` is not in the backend contract — the mapper reads it through
   * inferred aliases — so if the field is absent or zero board-wide this reads 0 and the tile is
   * not rendered, rather than showing a permanent, meaningless "0 over capacity". Do not surface
   * the count without checking this first.
   */
  roomsWithCapacity: number;
  /**
   * Stays with money still against them, and what that comes to.
   *
   * ⚠️ A DIFFERENT POPULATION FROM EVERY FIELD ABOVE — those count ROOMS, these count STAYS,
   * so they do not take part in the room arithmetic and must never be rendered as one more
   * equal tile in the strip.
   * so they do not participate in `total = occupied + available`.
   *
   * ⚠️ **CURRENT STAYS ONLY.** The board is `Reserved` / `Checked In`, so a guardian who
   * checked out owing money is not counted here at all. Any surface rendering these must say
   * so; the real total needs `get_boarding_outstanding_summary`.
   */
  owingCount: number;
  owingTotal: number;
  /** Stays whose figures never arrived. Reported separately — unknown is not settled. */
  owingUnknownCount: number;
}

/*
 * ⚠️ THERE WAS A SECOND `export interface BoardingKpiSet` HERE. DO NOT BRING IT BACK.
 *
 * The merge from `development` reintroduced the pre-rework declaration alongside the one above.
 * Because TypeScript MERGES same-name interfaces in the same scope, the two did not conflict —
 * they UNIONED, so `BoardingKpiSet` silently came to require six fields the implementation no
 * longer produces (`checkInsToday`, `checkOutsToday`, `availablePct`, `attentionPct`, and
 * `roomsInUse` + `pets` via `extends BoardingGroupKpis`). That is why the error landed on
 * `useBoardingKpis.ts` rather than here: the duplicate is legal TypeScript.
 *
 * Those tiles were removed deliberately, not lost:
 *  - **Attention** was structurally always zero — the backend defines it as "active boarding
 *    with a warning flag" and no such flag exists, so `attentionPct` could only ever be 0.
 *  - **Check-outs today** was dead — the board only holds `Reserved` / `Checked In`, so a
 *    checked-out stay is not on it to be counted.
 *  - `roomsInUse` / `pets` are per-GROUP figures and belong to `BoardingGroupKpis`, which is
 *    still used by `BoardingGroupSummaryCards`. The board-wide set has no business extending it.
 *
 * The type now follows the implementation, which is the direction the owner asked for.
 */
/** The KPI tiles that double as a filter on the room list below. `petsInHouse` deliberately does not. */
export type BoardingKpiFilterKey = "occupied" | "available" | "arrivalsToday" | "overCapacity";

/** Filter state for the hub's room table. `null` on a filter means "all". */
export interface BoardingRoomFilterState {
  search: string;
  /** Raw `room_type` (Suite | Kennel | Observation | Isolation | General). */
  roomType: string | null;
  /** Record status (Reserved | Checked In | …). */
  status: string | null;
  /**
   * Normalized occupancy (occupied | available | reserved).
   *
   * ⚠️ `attention` is NOT offered any more. No room can reach that state — the backend defines it
   * as "active boarding with a warning flag" and no such flag exists — so the option could only
   * ever return an empty list, which reads to the operator as "no results" rather than "this
   * does not apply to anything". The value is still accepted here because
   * `normalizeBoardingOccupancy` can technically produce it; it simply has no control.
   */
  occupancy: string | null;
  /** `YYYY-MM-DD`; matches a stay whose check-in OR check-out falls on that day. */
  date: string | null;
  /**
   * Room-capacity state. `"over"` keeps only rooms holding more animals than they report room for.
   * Set by the Over capacity KPI tile; there is no dropdown for it.
   */
  capacityState: "over" | null;
  group: "all" | BoardingRoomGroup;
  /**
   * Show only stays that still owe money — or whose figures never arrived.
   *
   * ⚠️ A switch, so `false` means "all", not "settled only". And it deliberately keeps the
   * UNKNOWN rows: see `boardingNeedsSettlement`. Hiding a stay we could not assess would answer
   * "who owes me?" by silently dropping the ones we were not told about.
   */
  owing: boolean;
}

/**
 * Payload shape for a billable line the client SENDS.
 *
 * Deliberately carries no `rate`, `amount` or `line_total`: the backend prices the line
 * from `item_code` + `qty`. The client authoring money and sending it as truth was
 * Tier-1 #2 in docs/BOARDING_GAPS.md — do not add those fields back. (The read model,
 * `BillableItem`, still has `amount` for display; the two shapes are separate on purpose.)
 */
export interface BoardingBillableItemWrite {
  /** Existing child-row name on update; omitted when adding a new line. */
  name?: string;
  item_code: string | null;
  item_name: string;
  qty: number;
  note?: string | null;
  linked_service_id?: string | null;
  item_type?: string;
}

/** A row rendered by the hub's room table — a flattened `BoardingUnitSummary` view model. */
export interface BoardingRoomRow {
  /** @deprecated Legacy ambiguous id — used only as a Vue `:key`. Never route an action through it. */
  id: string;
  roomId: RoomId | null;
  boardingRecordId: BoardingRecordId | null;
  roomCode: string;
  roomLabel: string | null;
  roomType: string | null;
  group: BoardingRoomGroup;
  status: BoardingOccupancyStatus;
  recordStatus: BoardingRecordStatus | null;
  occupancy: string;
}
